/**
 * Issue #14 — Directiva v-financial-tooltip (tarea 4.1.2)
 *
 * Uso:
 *   <span v-financial-tooltip="'razon_corriente'">Razón Corriente</span>
 *   <span v-financial-tooltip="{ key: 'margen_neto', placement: 'top' }">Margen</span>
 *
 * Inyecta un tooltip CSS posicionado con la explicación del término.
 * No depende de librerías externas: implementación CSS pura.
 */

import type { Directive, DirectiveBinding } from 'vue'
import { FINANCIAL_TOOLTIPS } from '@/composables/financial/useFinancialTooltips'

// ── Tipos ─────────────────────────────────────────────────────────────────────

type Placement = 'top' | 'bottom' | 'left' | 'right'

interface TooltipOptions {
  key: string
  placement?: Placement
  /** Texto plano override (cuando no hay clave en el catálogo) */
  text?: string
  /** Permite desactivar tooltip dinámicamente */
  disabled?: boolean
}

type TooltipValue = string | TooltipOptions

// ── Utilidades ────────────────────────────────────────────────────────────────

function resolveOptions(value: TooltipValue): TooltipOptions {
  if (typeof value === 'string') {
    return { key: value, placement: 'top' }
  }
  return { placement: 'top', ...value }
}

function buildTooltipEl(text: string, placement: Placement): HTMLElement {
  const el = document.createElement('div')
  el.className = `fin-tooltip fin-tooltip--${placement}`
  el.setAttribute('role', 'tooltip')
  el.setAttribute('aria-hidden', 'true')
  el.textContent = text
  return el
}

function positionTooltip(host: HTMLElement, tip: HTMLElement, placement: Placement) {
  const rect = host.getBoundingClientRect()
  const scrollX = window.scrollX
  const scrollY = window.scrollY

  const tipW = tip.offsetWidth
  const tipH = tip.offsetHeight

  const margin = 8

  let top = 0
  let left = 0

  switch (placement) {
    case 'top':
      top  = rect.top + scrollY - tipH - margin
      left = rect.left + scrollX + rect.width / 2 - tipW / 2
      break
    case 'bottom':
      top  = rect.bottom + scrollY + margin
      left = rect.left + scrollX + rect.width / 2 - tipW / 2
      break
    case 'left':
      top  = rect.top + scrollY + rect.height / 2 - tipH / 2
      left = rect.left + scrollX - tipW - margin
      break
    case 'right':
      top  = rect.top + scrollY + rect.height / 2 - tipH / 2
      left = rect.right + scrollX + margin
      break
  }

  tip.style.top  = `${Math.max(4, top)}px`
  tip.style.left = `${Math.max(4, left)}px`
}

// ── Directiva ─────────────────────────────────────────────────────────────────

interface TooltipEl extends HTMLElement {
  __finTip?: HTMLElement
  __finPlacement?: Placement
  __finHandlers?: {
    show: () => void
    hide: () => void
    focus: () => void
    blur: () => void
  }
}

function removeTooltip(el: TooltipEl): void {
  if (el.__finTip) {
    el.__finTip.remove()
    delete el.__finTip
  }
  if (el.__finHandlers) {
    const { show, hide } = el.__finHandlers
    el.removeEventListener('mouseenter', show)
    el.removeEventListener('mouseleave', hide)
    el.removeEventListener('focus', show)
    el.removeEventListener('blur', hide)
    delete el.__finHandlers
  }
  if (el.hasAttribute('aria-describedby')) {
    el.removeAttribute('aria-describedby')
  }
}

function mountTooltip(el: TooltipEl, text: string, placement: Placement): void {
  const tip = buildTooltipEl(text, placement)
  document.body.appendChild(tip)
  el.__finTip = tip
  el.__finPlacement = placement

  // Accesibilidad: enlazar host con tooltip
  const id = `fin-tip-${Math.random().toString(36).slice(2)}`
  tip.id = id
  el.setAttribute('aria-describedby', id)
  el.setAttribute('tabindex', el.getAttribute('tabindex') ?? '0')

  const show = () => {
    tip.style.display = 'block'
    requestAnimationFrame(() => positionTooltip(el, tip, placement))
    tip.setAttribute('aria-hidden', 'false')
  }

  const hide = () => {
    tip.style.display = 'none'
    tip.setAttribute('aria-hidden', 'true')
  }

  el.__finHandlers = { show, hide, focus: show, blur: hide }

  el.addEventListener('mouseenter', show)
  el.addEventListener('mouseleave', hide)
  el.addEventListener('focus', show)
  el.addEventListener('blur', hide)
}

function syncTooltip(el: TooltipEl, binding: DirectiveBinding<TooltipValue>): void {
  if (!binding.value) {
    removeTooltip(el)
    return
  }
  const opts = resolveOptions(binding.value)
  const placement = opts.placement ?? 'top'
  const entry = FINANCIAL_TOOLTIPS[opts.key]
  const tooltipText = opts.text ?? entry?.short ?? opts.key

  if (opts.disabled || !tooltipText) {
    removeTooltip(el)
    return
  }

  const currentText = el.__finTip?.textContent ?? ''
  const currentPlacement = el.__finPlacement
  if (el.__finTip && currentText === tooltipText && currentPlacement === placement) {
    return
  }

  removeTooltip(el)
  mountTooltip(el, tooltipText, placement)
}

export const vFinancialTooltip: Directive<TooltipEl, TooltipValue> = {
  mounted(el: TooltipEl, binding: DirectiveBinding<TooltipValue>) {
    syncTooltip(el, binding)
  },

  updated(el: TooltipEl, binding: DirectiveBinding<TooltipValue>) {
    syncTooltip(el, binding)
  },

  beforeUnmount(el: TooltipEl) {
    removeTooltip(el)
  },
}

// ── Estilos globales (inyectados una vez) ─────────────────────────────────────

if (typeof document !== 'undefined') {
  const STYLE_ID = 'fin-tooltip-styles'
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = `
      .fin-tooltip {
        display: none;
        position: absolute;
        z-index: 9999;
        max-width: 260px;
        padding: 0.5rem 0.75rem;
        background: #1e293b;
        color: #f1f5f9;
        font-size: 0.8rem;
        line-height: 1.5;
        border-radius: 8px;
        box-shadow: 0 4px 16px rgba(0,0,0,0.25);
        pointer-events: none;
        word-wrap: break-word;
      }
      .fin-tooltip::after {
        content: '';
        position: absolute;
        border: 5px solid transparent;
      }
      .fin-tooltip--top::after {
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-top-color: #1e293b;
      }
      .fin-tooltip--bottom::after {
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-bottom-color: #1e293b;
      }
      .fin-tooltip--left::after {
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-left-color: #1e293b;
      }
      .fin-tooltip--right::after {
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-right-color: #1e293b;
      }
    `
    document.head.appendChild(style)
  }
}
